import React, { useEffect, useState } from "react";
import { Key, Loader2, Check, Eye, EyeOff, Sparkles, Trophy, Flame } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import CardRewardModal from "./CardRewardModal";

function countdown(targetIso) {
  if (!targetIso) return "—";
  const ms = new Date(targetIso).getTime() - Date.now();
  if (ms <= 0) return "DROPPING…";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
}

const RARITY_COLOR = {
  common: "#9ca3af",
  uncommon: "#34d399",
  rare: "#60a5fa",
  mythic: "#c084fc",
  classified: "#ff453a",
};

/**
 * ArgCrackTheCode — homepage block surfacing today's puzzle.
 *
 * Rotates every UTC midnight. Each puzzle has a cipher + a hint. Every solver
 * gets a randomly-rolled Vault Card (40/30/15/10/5 odds across Common→Classified)
 * minted from the live catalog, so newly-released cards automatically join the
 * reward pool. There is NO coin fallback — even if the rolled tier is sold out
 * we fall through to lower tiers so you always leave with a card.
 *
 * Each user can solve once per day (atomic unique index in `arg_solves` on
 * `(user_id, day_index)`).
 */
export default function ArgCrackTheCode() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [lastSolve, setLastSolve] = useState(null); // holds the live solve response so the modal re-opens cleanly
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/arg/current")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  // Re-render every minute so the countdown stays accurate
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const solve = async () => {
    if (submitting) return;
    const a = (answer || "").trim();
    if (!a) { toast.error("Type your answer first"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/arg/current/solve", { answer: a });
      if (r.data.correct) {
        const streak = r.data.streak || 0;
        // Open the card-reveal modal (skip the tiny toast — the modal IS the celebration)
        if (r.data.reward?.card_name) {
          setLastSolve(r.data);
          setRewardOpen(true);
        } else {
          toast.success("SOLVED — card dispatched to your collection");
        }
        if (r.data.streak_bonus?.coins) {
          toast.success(`+${r.data.streak_bonus.coins} streak bonus`, { duration: 4500 });
        }
        if (streak >= 3) {
          toast.success(`${streak}-day streak — odds bumped`, { duration: 4500 });
        }
        const fresh = await api.get("/arg/current").then((rr) => rr.data);
        setData(fresh);
        setAnswer("");
        window.dispatchEvent(new Event("coins-changed"));
      } else {
        const left = r.data.attempts_remaining_today ?? 0;
        if (left > 0) {
          toast.error(`Wrong — ${left} attempt${left === 1 ? "" : "s"} left on this puzzle`);
        } else {
          toast.error("Wrong — out of attempts on this puzzle. New puzzle tomorrow.");
        }
        setData((d) => ({
          ...d,
          attempts_used_today: r.data.attempts_used_today,
          attempts_remaining_today: left,
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit");
      if (e?.response?.status === 429) {
        setData((d) => ({ ...d, attempts_remaining_today: 0 }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="border border-[#ffd60a]/40 p-5 flex items-center gap-2 text-zinc-500">
        <Loader2 size={12} className="animate-spin" /> <span className="font-mono text-[10px] tracking-[0.2em]">LOADING TODAY&apos;S PUZZLE…</span>
      </section>
    );
  }
  if (!data) return null;

  const solved = !!data.my_solved;
  const reward = data.my_solved?.reward;
  const nextDrop = data.next_drop_at || data.next_week_at;
  // tick is used in the countdown render — referenced to avoid the unused-var lint
  void tick;

  return (
    <section
      data-testid="arg-crack-the-code"
      className="relative border-2 border-[#ffd60a]/50 overflow-hidden"
      style={{
        background: "linear-gradient(140deg, rgba(255,214,10,0.08), transparent 60%), #0a0a0a",
      }}
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='80' height='80' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }}
      />

      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#ffd60a] flex items-center gap-2">
            <Key size={11} /> // CRACK THE CODE · {data.title}
          </div>
          <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 flex items-center gap-3 flex-wrap">
            {data.my_streak > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[#ff9500]"
                data-testid="arg-streak-badge"
                title={`Solve the puzzle tomorrow to keep your ${data.my_streak}-day streak alive`}
              >
                <Flame size={11} className="-mb-0.5" />
                {data.my_streak}-DAY STREAK
              </span>
            )}
            <span data-testid="arg-solver-count">
              <span className="text-[#ffd60a]">{data.solver_count || 0}</span>
              <span className="text-zinc-600"> SOLVED TODAY</span>
            </span>
            <span className="text-zinc-700">·</span>
            <span>NEW PUZZLE IN {countdown(nextDrop)}</span>
          </div>
        </div>

        <div className="border border-white/15 bg-[#050505] p-4 sm:p-5 mb-4">
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">// CIPHER</div>
          <div className="font-heading text-2xl sm:text-3xl text-[#ffd60a] tracking-wide break-words leading-tight">
            {data.cipher}
          </div>
        </div>

        <div className="mb-4">
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            data-testid="arg-hint-toggle"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-[#ffd60a] transition-colors"
          >
            {reveal ? <EyeOff size={11} /> : <Eye size={11} />} {reveal ? "HIDE HINT" : "SHOW HINT"}
          </button>
          {reveal && (
            <p className="mt-2 text-sm text-zinc-300 italic leading-relaxed">
              {data.hint}
            </p>
          )}
        </div>

        {solved ? (
          <div className="border border-[#00ff88]/50 bg-[#00ff88]/5 p-4 flex items-start gap-3" data-testid="arg-solved-block">
            <Trophy size={20} className="text-[#00ff88] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg text-white">You cracked it.</div>
              {reward?.reward_type === "card" ? (
                <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-300 mt-1 uppercase">
                  Awarded ·{" "}
                  <span style={{ color: RARITY_COLOR[reward.card_rarity] || "#ffd60a" }}>
                    {reward.card_name} · {reward.card_rarity}
                  </span>
                  {" "}· Copy #{reward.edition_number}
                  {reward.edition_limit ? ` / ${reward.edition_limit}` : ""}
                </div>
              ) : (
                <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mt-1 uppercase">
                  Card minted to your collection · check Vault Cards
                </div>
              )}
              {(reward?.card_name || lastSolve?.reward?.card_name) && (
                <button
                  type="button"
                  onClick={() => {
                    if (!lastSolve) {
                      // Synthesize a minimal solve payload from the stored reward so the
                      // modal can re-open even after a page refresh.
                      setLastSolve({
                        correct: true,
                        reward,
                        streak: data.my_streak,
                      });
                    }
                    setRewardOpen(true);
                  }}
                  data-testid="arg-reopen-reward"
                  className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#00ff88] hover:text-white transition-colors"
                >
                  // SEE THE CARD →
                </button>
              )}
              <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 mt-2">
                // NEW PUZZLE IN {countdown(nextDrop)}
              </div>
            </div>
          </div>
        ) : !user ? (
          <div className="border border-white/15 p-4 text-sm text-zinc-400">
            Sign in to submit your answer and roll the card.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap" data-testid="arg-attempts-row">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">
                  ATTEMPTS
                </span>
                <div className="flex items-center gap-1">
                  {Array.from({ length: data.attempts_per_day || 3 }).map((_, i) => {
                    const used = (data.attempts_used_today || 0) > i;
                    return (
                      <span
                        key={i}
                        className={`w-2.5 h-2.5 rounded-full ${used ? "bg-[#ff453a]" : "bg-[#ffd60a]"}`}
                      />
                    );
                  })}
                </div>
              </div>
              <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
                {data.attempts_remaining_today > 0
                  ? `${data.attempts_remaining_today} OF ${data.attempts_per_day} LEFT`
                  : "OUT OF ATTEMPTS · NEW PUZZLE TOMORROW"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") solve(); }}
                disabled={data.attempts_remaining_today === 0}
                placeholder={data.attempts_remaining_today === 0 ? "Come back tomorrow…" : "Type your answer…"}
                data-testid="arg-answer-input"
                className="flex-1 min-w-[200px] bg-[#050505] border border-white/20 focus:border-[#ffd60a] outline-none px-3 py-2 font-mono text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={solve}
                disabled={submitting || data.attempts_remaining_today === 0}
                data-testid="arg-submit-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#ffd60a] text-[#050505] hover:bg-white font-mono text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {submitting ? "Checking…" : "Submit"}
              </button>
            </div>
            {/* Reward odds ribbon — small, brutalist, lists exact probabilities */}
            <div
              className="mt-3 flex items-center gap-1 flex-wrap font-mono text-[9px] tracking-[0.2em] uppercase"
              data-testid="arg-reward-odds"
            >
              <Sparkles size={10} className="text-[#ffd60a]" />
              <span className="text-zinc-500">
                {data.my_streak >= 3
                  ? `STREAK-BOOSTED ODDS · DAY ${data.my_streak + 1} TIER ·`
                  : "RANDOM CARD ON SOLVE ·"}
              </span>
              {(data.reward_rarity_odds || []).map((r, i, arr) => (
                <span key={r.rarity} style={{ color: RARITY_COLOR[r.rarity] || "#ffd60a" }}>
                  {r.weight}% {r.rarity}{i < arr.length - 1 ? " · " : ""}
                </span>
              ))}
            </div>
            {data.my_streak >= 0 && data.my_streak < 30 && (
              <p className="mt-2 font-mono text-[9px] tracking-[0.18em] text-zinc-600">
                {data.my_streak < 3
                  ? `// SOLVE ${3 - data.my_streak} MORE DAY${3 - data.my_streak === 1 ? "" : "S"} IN A ROW TO UNLOCK THE 3-DAY ODDS TIER`
                  : data.my_streak < 7
                    ? `// ${7 - data.my_streak} MORE TO HIT THE 7-DAY TIER · +150 COIN BONUS WAITING`
                    : data.my_streak < 14
                      ? `// ${14 - data.my_streak} MORE TO HIT THE 14-DAY TIER · +300 COIN BONUS WAITING`
                      : `// ${30 - data.my_streak} MORE TO HIT THE 30-DAY TIER · +750 COIN BONUS WAITING`}
              </p>
            )}
          </>
        )}
      </div>
      {/* Reveal modal — fires once on solve, can be re-opened from the
          solved block. */}
      {(lastSolve?.reward || reward) && (
        <CardRewardModal
          open={rewardOpen}
          reward={lastSolve?.reward || reward}
          source="arg"
          streak={lastSolve?.streak ?? data.my_streak}
          streakBonus={lastSolve?.streak_bonus}
          onClose={() => setRewardOpen(false)}
        />
      )}
    </section>
  );
}
